// HTML sanitizer for rich-text task descriptions + comments.
//
// Descriptions/comments are now stored as HTML and rendered into other staff
// members' browsers, so unsanitized HTML is a stored-XSS hole. The backend is
// Cloudflare Workers (no DOM), so server-side scrubbing isn't practical — the
// real security boundary is sanitize-on-RENDER: every client cleans the HTML
// right before showing it (sanitizeRichHtml), regardless of what's stored. We
// also run it on save (normalizeRichHtml) to keep stored data tidy, but that's
// hygiene, not the boundary (a client save call is bypassable).
//
// The editor only ever emits: formatting tags (b/i/u/s/mark), a mention chip
// (<span class="mention" data-staff-id>), inline attachment images
// (<img data-attachment-id>), and color/highlight/size via inline style. We
// allow `style` but a hook keeps ONLY color / background-color / font-size, so
// no positioning/url()/expression tricks can ride in from pasted content.

import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'del', 'mark',
  'span', 'br', 'p', 'div', 'a', 'img', 'ul', 'ol', 'li', 'blockquote'
];

const ALLOWED_ATTR = [
  'style', 'class', 'href', 'title', 'target', 'rel', 'alt',
  'data-staff-id', 'data-attachment-id'
];

// The only CSS properties our formatting uses. Everything else is dropped.
const STYLE_PROPS = new Set(['color', 'background-color', 'font-size', 'font-family']);

let hookInstalled = false;
function installStyleFilter() {
  if (hookInstalled) return;
  hookInstalled = true;
  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName !== 'style') return;
    const safe = data.attrValue
      .split(';')
      .map(d => d.trim())
      .filter(Boolean)
      .filter(decl => {
        const idx = decl.indexOf(':');
        if (idx < 0) return false;
        const prop = decl.slice(0, idx).trim().toLowerCase();
        const val = decl.slice(idx + 1).toLowerCase();
        if (!STYLE_PROPS.has(prop)) return false;
        // No url()/expression()/imports/escapes/angle brackets in the value.
        return !/url\(|expression|javascript:|@import|[\\<>]/i.test(val);
      })
      .join('; ');
    data.attrValue = safe;
    if (!safe) data.keepAttr = false;
  });
}

const CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  // Drop unknown protocols on href/src (DOMPurify blocks javascript: etc.).
  ALLOW_UNKNOWN_PROTOCOLS: false,
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'svg', 'math'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'srcset']
};

/**
 * Clean HTML for display. Call this on EVERY render of stored description /
 * comment HTML, immediately before dangerouslySetInnerHTML. This is the XSS
 * boundary — never render stored HTML without it.
 */
export function sanitizeRichHtml(html: string): string {
  if (!html) return '';
  installStyleFilter();
  return DOMPurify.sanitize(html, CONFIG) as unknown as string;
}

/**
 * Same clean, run before saving so the DB never accumulates junk markup. Not a
 * security boundary on its own (a direct API call bypasses it) — sanitizeRichHtml
 * on render is. Also trims to '' when the content is visually empty so an empty
 * editor saves as null, not "<br>" / "<div></div>".
 */
export function normalizeRichHtml(html: string): string {
  const clean = sanitizeRichHtml(html);
  // contentEditable leaves <br>, &nbsp;, empty blocks behind for an "empty" box.
  const stripped = clean
    .replace(/<br\s*\/?>/gi, '')
    .replace(/<(p|div|span)[^>]*>\s*<\/\1>/gi, '')
    .replace(/&nbsp;| |\s/g, '');
  return stripped.length === 0 ? '' : clean;
}
